import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import DocumentTree from './components/DocumentTree';
import DocumentDetails from './components/DocumentDetails';
import VersionHistory from './components/VersionHistory';
import CollaborationPanel from './components/CollaborationPanel';
import AccessControlPanel from './components/AccessControlPanel';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const DocumentManagementCenter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('details');
  const [error, setError] = useState('');

  const documentTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'pdd', label: 'PDD' },
    { value: 'monitoring', label: 'Monitoring Reports' },
    { value: 'validation', label: 'Validation Documents' },
    { value: 'verification', label: 'Verification Documents' },
    { value: 'methodology', label: 'Methodologies' },
    { value: 'stakeholder', label: 'Stakeholder Documents' },
    { value: 'other', label: 'Other' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'indexed', label: 'Approved' },
    { value: 'processing', label: 'In Review' },
    { value: 'failed', label: 'Pending' }
  ];

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    loadDocuments();
  }, [user]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        ?.from('project_documents')
        ?.select(`
          *,
          user_profiles!project_documents_user_id_fkey(full_name, email),
          chat_projects!project_documents_project_id_fkey(name)
        `)
        ?.order('updated_at', { ascending: false });

      if (fetchError) throw fetchError;

      setDocuments(data || []);
      if (data?.length > 0 && !selectedDocument) {
        setSelectedDocument(data?.[0]);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentSelect = (doc) => {
    setSelectedDocument(doc);
    setActiveTab('details');
  };

  const handleDocumentUpdate = async () => {
    await loadDocuments();
    if (selectedDocument) {
      const updated = documents?.find(d => d?.id === selectedDocument?.id);
      if (updated) setSelectedDocument(updated);
    }
  };

  const filteredDocuments = documents?.filter(doc => {
    const matchesSearch = doc?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         doc?.type?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesType = filterType === 'all' || doc?.type === filterType;
    const matchesStatus = filterStatus === 'all' || doc?.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="pt-16">
        <div className="h-[calc(100vh-4rem)] flex">
          {/* Left Panel - Document Tree */}
          <div className="w-96 border-r border-border bg-card flex flex-col">
            <div className="p-4 border-b border-border">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                  <Icon name="FolderTree" size={20} color="var(--color-primary)" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold font-mono">Document Center</h1>
                  <p className="text-xs text-muted-foreground caption">Version Control & Collaboration</p>
                </div>
              </div>

              <Input
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="mb-3"
              />

              <div className="grid grid-cols-2 gap-2">
                <Select
                  options={documentTypes}
                  value={filterType}
                  onChange={setFilterType}
                  placeholder="Type"
                />
                <Select
                  options={statusOptions}
                  value={filterStatus}
                  onChange={setFilterStatus}
                  placeholder="Status"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <p className="text-sm text-muted-foreground">Loading documents...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="p-4">
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                    {error}
                  </div>
                </div>
              ) : (
                <DocumentTree
                  documents={filteredDocuments}
                  selectedDocument={selectedDocument}
                  onDocumentSelect={handleDocumentSelect}
                />
              )}
            </div>

            <div className="p-4 border-t border-border">
              <Button
                fullWidth
                iconName="Upload"
                iconPosition="left"
                onClick={() => navigate('/project-detail-view')}
              >
                Upload New Document
              </Button>
            </div>
          </div>

          {/* Right Panel - Document Details */}
          <div className="flex-1 flex flex-col bg-background">
            {selectedDocument ? (
              <>
                <div className="border-b border-border bg-card">
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h2 className="text-xl font-semibold mb-1">{selectedDocument?.name}</h2>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Icon name="FileText" size={14} />
                            {selectedDocument?.type?.toUpperCase()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="Calendar" size={14} />
                            {new Date(selectedDocument?.updated_at)?.toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="User" size={14} />
                            {selectedDocument?.user_profiles?.full_name || 'Unknown'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" iconName="Download">
                          Download
                        </Button>
                        <Button variant="outline" size="sm" iconName="Share2">
                          Share
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 border-b border-border -mb-px">
                      {[
                        { id: 'details', label: 'Details', icon: 'FileText' },
                        { id: 'versions', label: 'Version History', icon: 'GitBranch' },
                        { id: 'collaboration', label: 'Collaboration', icon: 'MessageSquare' },
                        { id: 'access', label: 'Access Control', icon: 'Shield' }
                      ]?.map(tab => (
                        <button
                          key={tab?.id}
                          onClick={() => setActiveTab(tab?.id)}
                          className={`
                            flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors
                            border-b-2 -mb-px
                            ${activeTab === tab?.id
                              ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                            }
                          `}
                        >
                          <Icon name={tab?.icon} size={16} />
                          {tab?.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                  {activeTab === 'details' && (
                    <DocumentDetails
                      document={selectedDocument}
                      onUpdate={handleDocumentUpdate}
                    />
                  )}
                  {activeTab === 'versions' && (
                    <VersionHistory
                      document={selectedDocument}
                      onUpdate={handleDocumentUpdate}
                    />
                  )}
                  {activeTab === 'collaboration' && (
                    <CollaborationPanel
                      document={selectedDocument}
                      onUpdate={handleDocumentUpdate}
                    />
                  )}
                  {activeTab === 'access' && (
                    <AccessControlPanel
                      document={selectedDocument}
                      onUpdate={handleDocumentUpdate}
                    />
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-muted-foreground">
                  <Icon name="FileText" size={64} className="mx-auto mb-4 opacity-20" />
                  <p className="text-lg font-medium mb-2">No Document Selected</p>
                  <p className="text-sm">Select a document from the left panel to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentManagementCenter;